import React from 'react';
import Tool, {
  type ToolArgs,
  type ToolArgsWithController,
} from '../../../tools/Tool';
import TableUtils from '../TableUtils';
import { type Editor, Node, type Path, Transforms } from 'slate';
import { type TableCellElement } from '../../../types/aha-editor';
import { getTableStructureFromSelection } from '../TableStructure';
import ColorUtils from '../../../util/color';
import TableCellBackgroundColorToolbarComponent from '../components/TableCellBackgroundColorToolbarComponent';

const getCurrentColor = (editor: Editor): undefined | string => {
  const path = TableUtils.currentCellPath(editor);
  if (!path) return;

  const node = Node.get(editor, path) as TableCellElement;
  const color = node.table_cellBackgroundColor;

  if (color) {
    return ColorUtils.mapColorFromColorPickerToDarkMode(color);
  }
};

const setCellColor = (editor: Editor, path: Path, color?: string) => {
  if (color) {
    Transforms.setNodes(
      editor,
      { table_cellBackgroundColor: color },
      { at: path }
    );
  } else {
    Transforms.unsetNodes(editor, 'table_cellBackgroundColor', {
      at: path,
    });
  }
};

class ToolCellBackgroundColor extends Tool {
  static toolName = 'Cell background color';

  static action(args: ToolArgs) {
    const { editor, controller } = args;

    const tableStructure = getTableStructureFromSelection(editor);
    if (!tableStructure) return;

    const tablePath = TableUtils.currentTablePath(editor);
    if (!tablePath) return;

    const { tableSelection } = controller.state.pluginState;

    if (tableSelection.multipleCellsSelected) {
      tableSelection.selectedCellPaths.forEach((cellPath: Path) => {
        const coords = tableStructure.pathToCoordinates(cellPath);
        if (!coords) return;

        const cellInfo = tableStructure.cell(
          coords.columnIndex,
          coords.rowIndex
        );
        if (cellInfo?.isActual) {
          const cellPath = [...tablePath, ...cellInfo.relativePath];
          setCellColor(editor, cellPath, args.color);
        }
      });
    } else {
      const cellPath = TableUtils.currentCellPath(editor);
      if (!cellPath) return;

      setCellColor(editor, cellPath, args.color);
    }
  }

  static renderToolbarComponent(toolArgs: ToolArgsWithController): JSX.Element {
    const { editor, controller } = toolArgs;

    const setBackgroundColor = (color?: string) => {
      ToolCellBackgroundColor.action({
        editor,
        controller,
        color,
      });
    };

    return (
      <TableCellBackgroundColorToolbarComponent
        editor={editor}
        getBackgroundColor={() => getCurrentColor(editor)}
        setBackgroundColor={setBackgroundColor}
        active={this.isActive(toolArgs)}
        enabled={this.isEnabled(toolArgs)}
      />
    );
  }
}

export default ToolCellBackgroundColor;
